import { z } from "zod";
import { TranslationMapSchema } from "./utils";
import { ScheduledSurveySummarySchema } from "./studies";
import {
  ScheduleInputSchema,
  ScheduleSchema,
  ScheduledSurveyStateSchema,
  SurveyOccurrenceSchema,
  TriggerSurveyInputSchema,
} from "./surveySchedule";

/**
 * A questionnaire a clinician schedules for ONE patient, outside any study.
 *
 * A study is a scientific instrument: a frozen protocol, a cohort to compare
 * across, a consent form. Routine monitoring is none of those — a nurse wants
 * this patient to answer a wellbeing questionnaire every Monday, and wants to
 * change their mind next month. So it is not a study of one, it is the other
 * kind of owner a `SurveyPlan` can have.
 *
 * What that buys, and what it costs:
 *
 *   - no consent. There is structurally nothing to sign: a consent form is a
 *     field on a Study. Submission is gated on the occurrence being open and
 *     nothing else, and the reminder SMS works exactly as it does for a study.
 *   - no freeze, and therefore APPEND AND STOP rather than edit. A schedule is
 *     written once and stopped; "change the cadence" is stop plus create. A
 *     stored answer names its occurrence by INDEX, so editing a live rule in
 *     place would silently repoint answers a patient has already given, which
 *     is the one thing the derived-occurrence design must never allow.
 *   - misses count from the moment it was scheduled, where an unconsented
 *     study enrolment reads as "nothing owed yet".
 */

/**
 * `key` and `label` are OPTIONAL here and required on a study's schedule, which
 * is a deliberate asymmetry rather than an oversight.
 *
 * Both exist to disambiguate the same instrument appearing several times: a
 * protocol collecting PROMIS-29 three times needs three names a patient can
 * tell apart and three export columns. Scheduling one questionnaire for one
 * patient usually has nothing to disambiguate, so the server defaults the label
 * to the survey's own title and derives a key from it. A clinician who does
 * schedule the same questionnaire twice can still name them.
 */
export const CreatePatientSurveyInputSchema = z.object({
  surveyId: z.string(),
  key: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9-]*$/, "Expected a lowercase slug, e.g. weekly")
    .optional(),
  label: TranslationMapSchema.optional(),
  schedule: ScheduleInputSchema,
});
export type CreatePatientSurveyInput = z.infer<
  typeof CreatePatientSurveyInputSchema
>;

/**
 * One monitoring schedule as the DASHBOARD sees it: the rule a clinician wrote,
 * plus the occurrences it currently resolves to for this patient.
 *
 * The occurrence state is `ScheduledSurveyStateSchema` verbatim, spread in
 * rather than nested, because it is the same question with the same answer as
 * inside a study and the patient app reads the two through one code path.
 */
export const PatientSurveySchema = ScheduledSurveyStateSchema.extend({
  survey: ScheduledSurveySummarySchema,
  schedule: ScheduleSchema,
  createdAt: z.coerce.date(),
  /**
   * Set once and never cleared. A stopped schedule is still listed, because it
   * is what the answers already collected hang off and hiding it would make a
   * patient's history look like it never happened.
   */
  stoppedAt: z.coerce.date().nullable(),
});
export type PatientSurvey = z.infer<typeof PatientSurveySchema>;

export const PatientSurveyListSchema = z.object({
  surveys: z.array(PatientSurveySchema),
});
export type PatientSurveyList = z.infer<typeof PatientSurveyListSchema>;

/**
 * What the PATIENT APP gets: the occurrence state and the survey title, and
 * deliberately not the rule.
 *
 * A patient needs to know what is open and when it shuts. `repeatEvery: 2,
 * repeatUnit: "WEEK"` is a clinician's sentence about a protocol, and rendering
 * it would mean translating five orthogonal fields into prose on a phone.
 */
export const PatientSurveyStateSchema = ScheduledSurveyStateSchema;
export type PatientSurveyState = z.infer<typeof PatientSurveyStateSchema>;

export const PatientSurveyStateListSchema = z.object({
  surveys: z.array(PatientSurveyStateSchema),
});
export type PatientSurveyStateList = z.infer<
  typeof PatientSurveyStateListSchema
>;

const PatientSurveyParamsSchema = z.object({
  patientId: z.string(),
  scheduledSurveyId: z.string(),
});

export const listPatientSurveysSchemas = {
  params: z.object({ patientId: z.string() }),
  response: { 200: PatientSurveyListSchema },
};

export const createPatientSurveySchemas = {
  params: z.object({ patientId: z.string() }),
  body: CreatePatientSurveyInputSchema,
  response: { 201: PatientSurveySchema },
};

export const stopPatientSurveySchemas = {
  params: PatientSurveyParamsSchema,
  response: { 200: PatientSurveySchema },
};

export const triggerPatientSurveySchemas = {
  params: PatientSurveyParamsSchema,
  body: TriggerSurveyInputSchema,
  response: { 201: SurveyOccurrenceSchema },
};

/**
 * Texting the open occurrence's link again.
 *
 * It opens NOTHING: a trigger is another occurrence, this is another SMS about
 * the one already open, which is what a patient who deleted the message or
 * changed handset needs. So it names no occurrence in the request either. The
 * open one is re-derived server-side and there is at most one by construction,
 * so a key on the wire could only ever disagree with the engine.
 *
 * `sentAt` is the instant the provider accepted it, which is evidence of an
 * ATTEMPT and never of delivery.
 */
export const resendPatientSurveySchemas = {
  params: PatientSurveyParamsSchema,
  response: { 200: z.object({ sentAt: z.coerce.date() }) },
};
